import { type ReactNode, useState, useEffect } from "react";
import type { EditorConfig, EditorValues, UploadedImage, ExportSettings } from "../types";
import { EditorSidebar } from "./EditorSidebar";
import { ImagePreview } from "./ImagePreview";
import { UploadArea } from "./UploadArea";
import { luminaService } from "../services/luminaService";
import { ToastProvider, useToast } from "../../ui/components";

export interface ImageEditorProps {
  config: EditorConfig;
  onExport?: (
    values: EditorValues,
    image: UploadedImage,
    settings: ExportSettings
  ) => Promise<void>;
  header?:
    | ReactNode
    | ((props: { hasImage: boolean; onPreviewUpdate?: (url: string) => void }) => ReactNode);
}

const DEFAULT_VALUES: EditorValues = {
  globalLight: {
    exposure: 0,
    brightness: 0,
    contrast: 0,
  },
  regionalLight: {
    shadows: 0,
    highlights: 0,
    midtones: 0,
  },
  color: {
    saturation: 0,
    temperature: 0,
    tint: 0,
  },
  tonalCurve: {
    points: [
      { x: 0, y: 0 }, // Locked start point
      { x: 1, y: 1 }, // Locked end point
    ],
  },
  colorGrading: {
    shadows: { hue: 0, saturation: 0, blend: 0 },
    midtones: { hue: 0, saturation: 0, blend: 0 },
    highlights: { hue: 0, saturation: 0, blend: 0 },
  },
};

function ImageEditorContent({ config, onExport, header }: ImageEditorProps) {
  const [image, setImage] = useState<UploadedImage | null>(null);
  const [values, setValues] = useState<EditorValues>(DEFAULT_VALUES);
  const [isInitializing, setIsInitializing] = useState(true);
  const [initializationError, setInitializationError] = useState<string | null>(null);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
  const [isShowingOriginal, setIsShowingOriginal] = useState(false);
  const toast = useToast();

  // Initialize Lumina service on component mount
  useEffect(() => {
    const initializeService = async () => {
      try {
        await luminaService.initialize();
        setInitializationError(null);
      } catch (error) {
        console.error("Failed to initialize Lumina service:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to initialize the image engine";
        setInitializationError(errorMessage);
        toast.showError("Initialization Failed", errorMessage, 0);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeService();
  }, [toast]);

  const handleImageUpload = (uploadedImage: UploadedImage) => {
    setImage(uploadedImage);
    setCurrentImageUrl(uploadedImage.url);
    setValues(DEFAULT_VALUES);
    toast.showSuccess(
      "Image Loaded",
      `${uploadedImage.file.name} (${uploadedImage.width}x${uploadedImage.height})`
    );
  };

  const handleValuesChange = (newValues: Partial<EditorValues>) => {
    setValues((prev) => ({
      ...prev,
      ...newValues,
    }));
  };

  const handleExport = async (settings: ExportSettings) => {
    if (!image) {
      return;
    }

    // Use custom export handler if provided, otherwise use built-in SDK export
    if (onExport) {
      await onExport(values, image, settings);
    } else {
      // Default SDK export functionality
      try {
        const blob = await luminaService.exportImage(settings);

        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const extension = settings.format === "jpeg" ? "jpg" : settings.format;
        const fileName = image.file.name.replace(/\.[^/.]+$/, "");
        link.download = `edited-${fileName}_q${settings.quality}.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.showSuccess(
          "Export Complete",
          `Exported the image as ${extension.toUpperCase()} at ${settings.quality}% quality`
        );
      } catch (error) {
        console.error("Export failed:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to export image";
        toast.showError("Export Failed", errorMessage);
      }
    }
  };

  const handleCloseImage = () => {
    setImage(null);
    setCurrentImageUrl(null);
    setValues(DEFAULT_VALUES);
  };

  if (isInitializing) {
    return (
      <div className="flex h-screen flex-col bg-muted">
        {header && (
          <div className="flex-shrink-0">
            {typeof header === "function"
              ? (
                  header as (props: {
                    hasImage: boolean;
                    onPreviewUpdate?: (url: string) => void;
                  }) => ReactNode
                )({ hasImage: false, onPreviewUpdate: setCurrentImageUrl })
              : header}
          </div>
        )}
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent mx-auto mb-4" />
            <div className="text-muted-foreground text-lg">Initializing image editor...</div>
            <div className="text-muted-foreground text-sm mt-2">Loading WASM modules</div>
          </div>
        </div>
      </div>
    );
  }

  if (initializationError) {
    return (
      <div className="flex h-screen flex-col bg-muted">
        {header && (
          <div className="flex-shrink-0">
            {typeof header === "function"
              ? (
                  header as (props: {
                    hasImage: boolean;
                    onPreviewUpdate?: (url: string) => void;
                  }) => ReactNode
                )({ hasImage: false, onPreviewUpdate: setCurrentImageUrl })
              : header}
          </div>
        )}
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center max-w-md">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <div className="text-foreground text-xl font-semibold mb-2">
              Failed to Initialize Editor
            </div>
            <div className="text-muted-foreground text-sm mb-4">
              The editor could not load the image processing engine. Possible causes:
            </div>
            <div className="text-left text-muted-foreground text-sm mb-4 space-y-1">
              <div>• The browser is incompatible</div>
              <div>• The network connection failed</div>
              <div>• The browser does not support WASM</div>
            </div>
            <div className="rounded border border-red-500/30 bg-red-500/10 p-3 text-left">
              <div className="text-red-900 dark:text-red-100 text-sm font-medium">
                Error Details:
              </div>
              <div className="text-red-700 dark:text-red-200 text-xs mt-1 font-mono break-words">
                {initializationError}
              </div>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-muted">
      {/* Optional Header */}
      {header && (
        <div className="flex-shrink-0">
          {typeof header === "function"
            ? (
                header as (props: {
                  hasImage: boolean;
                  onPreviewUpdate?: (url: string) => void;
                }) => ReactNode
              )({ hasImage: !!image, onPreviewUpdate: setCurrentImageUrl })
            : header}
        </div>
      )}

      {/* Editor Content */}
      {!image ? (
        // No image loaded - show upload area full screen
        <div className="flex flex-1 items-center justify-center">
          <UploadArea onImageUpload={handleImageUpload} />
        </div>
      ) : (
        // Image loaded - show editor with sidebar
        <div className="flex flex-1 flex-col overflow-hidden md:flex-row">
          {/* Image Area - Fixed, non-scrollable */}
          <div className="flex h-[60%] flex-1 items-center justify-center overflow-hidden md:order-2 md:h-full">
            <ImagePreview
              image={image}
              values={values}
              onClose={handleCloseImage}
              originalImageUrl={luminaService.getOriginalImageUrl() ?? undefined}
              currentImageUrl={currentImageUrl ?? undefined}
              isShowingOriginal={isShowingOriginal}
              onToggleOriginal={() => setIsShowingOriginal(!isShowingOriginal)}
            />
          </div>

          {/* Sidebar - Constrained height, internally scrollable */}
          <div className="h-[40%] w-full flex-shrink-0 bg-background md:order-1 md:h-full md:w-[25vw]">
            <EditorSidebar
              config={config}
              values={values}
              onChange={handleValuesChange}
              onExport={config.allowExport ? handleExport : undefined}
              onPreviewUpdate={setCurrentImageUrl}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function ImageEditor(props: ImageEditorProps) {
  return (
    <ToastProvider>
      <ImageEditorContent {...props} />
    </ToastProvider>
  );
}
